import React, { useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import { caseInsensitiveComparator } from "utils/comparatorUtils";
import { valueStrings } from "constants/constantStrings";

import TableTooltip from "./TableTooltip";

import "./index.scss";

const AgTable = ({
  gridRef,
  rowData,
  columnDefs,
  quickFilterText,
  context,
  onRowClicked,
  onCellClicked,
  customHeight,
  frameworkComponents,
  editType,
  getRowNodeId,
  onSelectionChanged,
  rowHeight,
  onGridReady,
  suppressRowClickSelection,
  rowSelection,
  onRowSelected,
  setBadgeCount,
  suppressScrollOnNewData,
  customTableClass
}) => {
  const minimumHeight = "210px";
  customHeight = rowData?.length < 4 ? minimumHeight : customHeight;
  customHeight =
    customTableClass || rowData?.length === 0 || (rowData?.length > 4 && rowData?.length < 7 && customHeight > minimumHeight)
      ? "auto"
      : customHeight || "calc(100vh - 200px)";

  rowHeight = rowHeight || 37;
  suppressRowClickSelection = suppressRowClickSelection || true;
  rowSelection = rowSelection || "multiple";
  frameworkComponents = { ...frameworkComponents, customTooltip: TableTooltip };
  customTableClass = customTableClass || "ag-theme-custom-react";

  const [columns, setColumns] = useState([]);
  const [colState, setColumnState] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const agGridRef = useRef();

  const getGridAPI = () => {
    const ref = gridRef || agGridRef;
    return ref?.current?.api;
  };

  useEffect(() => {
    if (columnDefs) {
      columnDefs = columnDefs?.map(col => {
        const gridAPI = getGridAPI();
        if (gridAPI && Object.hasOwn(col, "filter") && !col.filter) {
          gridAPI.setFilterModel(null);
        }
        if (!col.cellRendererFramework) {
          if (col.field) {
            col.tooltipField = col.field;
          } else {
            col.tooltipValueGetter = params => params.value || params.valueFormatted;
          }
          col.tooltipComponent = "customTooltip";
        }
        if (col.valueFormatter) {
          col.valueGetter = col.valueFormatter;
        }
        if (rowData.length === 0) {
          col.sortable = false;
        }
        return col;
      });
      setColumns(columnDefs);
    }
  }, [columnDefs]);

  useEffect(() => {
    if (columnApi && colState) {
      columnApi.applyColumnState(colState);
    }
  }, [rowData]);

  const onAgGridReady = params => {
    setColumnApi(params.columnApi);
    if (onGridReady) {
      onGridReady(params);
    }
  };

  const onSortChanged = par => {
    setColumnState(par.columnApi.getColumnState());
  };

  const defaultColDef = {
    editable: true,
    sortable: true,
    unSortIcon: true,
    icons: {
      sortAscending: '<i class="fas fa-sort-up"/>',
      sortDescending: '<i class="fas fa-sort-down"/>',
      sortUnSort: '<i class="fas fa-sort"/>'
    },
    sortingOrder: ["asc", "desc"],
    filter: false,
    filterParams: {
      buttons: ["apply", "reset"],
      closeOnApply: true,
      newRowsAction: "keep"
    },
    comparator: caseInsensitiveComparator,
    suppressMenu: rowData.length === 0
  };
  const onFilterChanged = event => {
    const resultsCount = event.api.getDisplayedRowCount();
    if (setBadgeCount) setBadgeCount(resultsCount.toString());
    const ref = getGridAPI();
    if (ref) {
      if (resultsCount === 0) {
        ref.showNoRowsOverlay();
      } else {
        ref.hideOverlay();
      }
    }
  };

  const gridOptions = { rowHeight, editType, rowSelection, suppressRowClickSelection, suppressMultiSort: true };

  const rowClickHandler = event => {
    if (!window.isTableCellAction && onRowClicked) {
      onRowClicked(event);
    }
  };

  return (
    <div className={`${customTableClass}  ${onRowClicked ? "row-pointer" : ""}`} style={{ height: customHeight }}>
      <AgGridReact
        ref={gridRef || agGridRef}
        domLayout={customHeight === "auto" ? "autoHeight" : "normal"}
        rowData={rowData}
        quickFilterText={quickFilterText}
        onRowClicked={rowClickHandler}
        IdentityCheck="IdentityCheck"
        gridOptions={gridOptions}
        onCellClicked={onCellClicked}
        columnDefs={columns}
        defaultColDef={defaultColDef}
        context={context}
        frameworkComponents={frameworkComponents}
        getRowNodeId={getRowNodeId}
        onRowSelected={onRowSelected}
        suppressClickEdit={true}
        onSelectionChanged={onSelectionChanged}
        onGridReady={onAgGridReady}
        onRowDataUpdated={onFilterChanged}
        onFilterChanged={onFilterChanged}
        tooltipShowDelay={500}
        onSortChanged={onSortChanged}
        overlayNoRowsTemplate={`<span>${valueStrings.noData}</span>`}
        suppressScrollOnNewData={suppressScrollOnNewData}
        suppressDragLeaveHidesColumns={true}
        // tooltipHideDelay={5000}
      />
    </div>
  );
};

export default AgTable;
